import React from "react";
import { motion } from "framer-motion";
import {
  Inbox, ShoppingCart, HeartPulse, Home as HomeIcon,
  Shield, Wallet, TrendingUp, Sparkles, ArrowRight,
} from "lucide-react";
import PageMeta from "../site/PageMeta";
import { calendlyUrl, calendlyLinkProps } from "../../lib/calendly";

const services = [
  {
    icon: Inbox,
    title: "Admin Support",
    body: "Day-to-day administrative work — scheduling, inbox management, documentation, and the operational tasks that pile up without dedicated support.",
  },
  {
    icon: ShoppingCart,
    title: "eCommerce Virtual Assistant Support",
    body: "Listing management, order support, and customer communication for online sellers who need reliable hands without a full internal team.",
  },
  {
    icon: HeartPulse,
    title: "Healthcare Customer Service",
    body: "Patient-facing and administrative support handled with the discretion and consistency healthcare work requires.",
  },
  {
    icon: HomeIcon,
    title: "Real Estate Support",
    body: "Transaction coordination, listing support, and client communication for agents and brokerages managing high volume.",
  },
  {
    icon: Shield,
    title: "Insurance BPO Services",
    body: "Policy servicing, claims support, and back-office processing for insurance operations that need to move at volume without errors.",
  },
  {
    icon: Wallet,
    title: "Merchant Cash Advance Services",
    body: "Application processing, underwriting support, and servicing for MCA providers who need throughput without sacrificing accuracy.",
  },
  {
    icon: TrendingUp,
    title: "Business Development",
    body: "Outbound outreach and pipeline support to keep growth efforts moving without pulling your team off their core work.",
  },
];

export default function BPO() {
  return (
    <>
      <PageMeta
        title="BPO — Business Process Outsourcing | A4 Assist"
        description="Operations support that doesn't need hand-holding. Admin, eCommerce, healthcare, real estate, insurance, MCA, and BD — AI-assisted, specialist-owned."
      />

      <section className="pt-36 pb-14">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="text-[10px] tracking-[0.28em] uppercase font-bold text-[#A3FF5E] mb-4">
            BPO — Business Process Outsourcing
          </div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-[-0.02em] text-white leading-[1.05] max-w-4xl">
            Operations support that doesn't need{" "}
            <span className="font-serif italic">hand-holding</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-base sm:text-lg text-slate-400 leading-relaxed">
            Every business has work that has to get done but doesn't need to
            be done by your core team. Our BPO services cover that work
            reliably, with AI-assisted tools handling the routine parts so
            our specialists focus on what needs judgment.
          </p>
        </div>
      </section>

      <section className="pb-16">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {services.map((s, i) => (
            <motion.div
              key={s.title}
              initial={{ opacity: 0, y: 14 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.45, delay: i * 0.04 }}
              className="rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-6 hover:border-[#A3FF5E]/40 transition-colors"
              data-testid={`bpo-service-${i}`}
            >
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-[#A3FF5E]/10 text-[#A3FF5E] border border-[#A3FF5E]/20">
                <s.icon size={18} />
              </div>
              <h3 className="font-display mt-5 text-base font-semibold text-white">
                {s.title}
              </h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-400">
                {s.body}
              </p>
            </motion.div>
          ))}
        </div>
      </section>

      <section className="pb-24 sm:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <div className="rounded-2xl border border-[#00E5FF]/25 bg-gradient-to-br from-[#0B1A33] to-[#071326] p-8 sm:p-10 relative overflow-hidden">
            <div className="absolute -top-24 -right-16 h-64 w-64 rounded-full bg-[#00E5FF]/10 blur-3xl" />
            <div className="relative flex items-start gap-5">
              <div className="hidden sm:inline-flex h-12 w-12 items-center justify-center rounded-xl bg-[#00E5FF]/15 text-[#00E5FF] border border-[#00E5FF]/30 shrink-0">
                <Sparkles size={22} />
              </div>
              <div>
                <span className="chip-ai">AI at Work</span>
                <h3 className="font-display mt-3 text-2xl font-semibold text-white">
                  Where it makes sense, our support teams use AI-assisted tools
                </h3>
                <p className="mt-2 text-slate-400">
                  We use AI-assisted tools to triage requests and draft
                  first-pass responses — a specialist reviews anything that
                  isn't routine before it goes out.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="pb-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-8">
          <div>
            <h3 className="font-display text-xl sm:text-2xl font-semibold text-white">
              Have operational work piling up?
            </h3>
            <p className="mt-1.5 text-sm text-slate-400 max-w-lg">
              Tell us what's slipping and we'll help you figure out what to
              cover and where to start.
            </p>
          </div>
          <div className="flex gap-3">
            <a
              href={calendlyUrl({ pillar: "bpo" })}
              {...calendlyLinkProps}
              data-testid="bpo-contact-cta"
              className="group inline-flex items-center gap-2 bg-[#00E5FF] hover:bg-[#00E5FF]/90 text-[#050B14] font-semibold px-6 py-3.5 rounded-md shadow-[0_0_30px_-8px_rgba(0,229,255,0.6)]"
            >
              Get Free Audit <ArrowRight size={16} className="arrow-shift" />
            </a>
            <a
              href="mailto:sahil@a4assist.com?subject=A4%20Assist%20—%20BPO%20inquiry"
              className="inline-flex items-center gap-2 border border-slate-700 hover:border-[#00E5FF]/60 text-white font-semibold px-6 py-3.5 rounded-md"
            >
              Email Sahil
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
